"use client"

import { Box, Group, Text, Stack, ThemeIcon, useMantineTheme, UnstyledButton } from '@mantine/core'
import { useHover } from '@mantine/hooks'
import { ReactNode, forwardRef } from 'react'
import { IconType } from 'react-icons'
// @ts-ignore
import classes from './ActionCard.module.css'

export interface ActionCardProps {
  title: string
  description?: string
  icon?: IconType | ReactNode
  variant?: 'vertical' | 'horizontal' | 'compact' | 'hero' | 'minimal'
  color?: string
  onClick?: () => void
  disabled?: boolean
  children?: ReactNode
  className?: string
}

// Get sizes based on variant
const getVariantStyles = (variant: ActionCardProps['variant']) => {
  switch (variant) {
    case 'hero':
      return {
        minHeight: 380,
        padding: 56,
        iconSize: 56,
        titleSize: 'xl' as const,
        descriptionSize: 'lg' as const,
        iconPadding: 20,
      }
    case 'vertical':
      return {
        minHeight: 280,
        padding: 36,
        iconSize: 40,
        titleSize: 'lg' as const,
        descriptionSize: 'md' as const,
        iconPadding: 16,
      }
    case 'horizontal':
      return {
        minHeight: 160,
        padding: 28,
        iconSize: 32,
        titleSize: 'md' as const,
        descriptionSize: 'sm' as const,
        iconPadding: 12,
      }
    case 'compact':
      return {
        minHeight: 120,
        padding: 24,
        iconSize: 36,
        titleSize: 'md' as const,
        descriptionSize: 'sm' as const,
        iconPadding: 8,
      }
    case 'minimal':
      return {
        minHeight: 100,
        padding: 20,
        iconSize: 20,
        titleSize: 'sm' as const,
        descriptionSize: 'xs' as const,
        iconPadding: 8,
      }
    default:
      return {
        minHeight: 200,
        padding: 28,
        iconSize: 28,
        titleSize: 'md' as const,
        descriptionSize: 'sm' as const,
        iconPadding: 12,
      }
  }
}

export const ActionCard = forwardRef<HTMLButtonElement, ActionCardProps>(({
  title,
  description,
  icon,
  variant = 'vertical',
  color = 'gray',
  onClick,
  disabled = false,
  children,
  className,
  ...props
}, ref) => {
  const { hovered, ref: hoverRef } = useHover()
  const theme = useMantineTheme()
  const variantStyles = getVariantStyles(variant)
  const IconComponent = typeof icon === 'function' ? icon as IconType : null
  const isHorizontalLayout = ['horizontal', 'compact', 'minimal'].includes(variant || '')

  // Get theme colors
  const colorName = color as keyof typeof theme.colors
  const borderColor = hovered && !disabled
    ? theme.colors[colorName]?.[5] || theme.colors.gray[5]
    : theme.colors[colorName]?.[3] || theme.colors.gray[3]
  const backgroundColor = hovered && !disabled
    ? `var(--mantine-color-${colorName}-light-hover)` || `var(--mantine-color-gray-light-hover)` // light-hover
    : `var(--mantine-color-${colorName}-light)` || `var(--mantine-color-gray-light)` // light

  const cardClassName = [
    classes.card,
    classes[variant || 'default'],
    isHorizontalLayout ? classes.gapHorizontal : classes.gapVertical,
    disabled ? classes.disabled : '',
    className,
  ].filter(Boolean).join(' ')

  return (
    <UnstyledButton
      {...props}
      ref={ref}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={classes.button}
    >
      <Box
        ref={hoverRef}
        className={cardClassName}
        bd={`2px solid ${borderColor}`}
        bg={backgroundColor}
        mih={variantStyles.minHeight}
        p={variantStyles.padding}
      >
        {/* Content */}
        {isHorizontalLayout ? (
          <>
            {/* Icon */}
            {icon && (
              <ThemeIcon
                size={variantStyles.iconSize + variantStyles.iconPadding * 2}
                radius="md"
                color={color}
                c={color}
                variant="light"
              >
                {IconComponent ? (
                  <IconComponent size={variantStyles.iconSize} />
                ) : (
                  icon as ReactNode
                )}
              </ThemeIcon>
            )}

            {/* Text Content */}
            <Box className={classes.contentHorizontal}>
              <Group justify="space-between" align="flex-start">
                <Stack gap={variant === 'minimal' ? 'xs' : 'sm'}>
                  <Text size={variantStyles.titleSize} c={colorName} fw={600} lh={1.3}>
                    {title}
                  </Text>
                  {description && (
                    <Text size={variantStyles.descriptionSize} c="var(--mantine-color-text)" lh={1.4}>
                      {description}
                    </Text>
                  )}
                  {children}
                </Stack>
              </Group>
            </Box>
          </>
        ) : (
          <Stack
            gap={variant === 'hero' ? 'xl' : 'lg'}
            align="center"
            className={classes.contentVertical}
          >
            {/* Icon */}
            {icon && (
              <ThemeIcon
                size={variantStyles.iconSize + variantStyles.iconPadding * 2}
                radius="lg"
                color={color}
                c={color}
                variant="light"
              >
                {IconComponent ? (
                  <IconComponent size={variantStyles.iconSize} />
                ) : (
                  icon as ReactNode
                )}
              </ThemeIcon>
            )}

            {/* Text Content */}
            <Stack gap="sm" align="center" className={classes.textContent}>
              <Text size={variantStyles.titleSize} c={colorName} fw={600} lh={1.3}>
                {title}
              </Text>
              {description && (
                <Text size={variantStyles.descriptionSize} c="var(--mantine-color-text)" lh={1.4}>
                  {description}
                </Text>
              )}
              {children}
            </Stack>
          </Stack>
        )}
      </Box>
    </UnstyledButton>
  )
})

ActionCard.displayName = 'ActionCard'
